/**
 * Reset url after removing search param
 */
export const resetWindowLocationWithoutParams = (params: string[]) => {
  const currentUrl = window.location.href
  const updatedUrl = removeSearchParams(currentUrl, params)
  window.history.replaceState({ message: 'URL updated' }, '', updatedUrl)
}

/**
 * Checks if running in dev environment
 */
export const isDevEnvironment = () => {
  return (
    process.env.NODE_ENV === 'development' || window?.location?.hostname === 'localhost'
  )
}

/**
 * Checks if running in browser
 */
export const isBrowserEnvironment = (): boolean => {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

/**
 * Make sure browser supports SSE
 */
export const validateSSEBrowserSupport = (): boolean =>
  'fetch' in window && 'ReadableStream' in window

/**
 * Check permissions and remove unwanted parameters before copy
 */
export const copyLink = (text: string, params: string[]) => {
  let mergedParamsToRm = [...params, 'fbct'] //always remove token
  const updated = removeSearchParams(text, mergedParamsToRm)
  return navigator.clipboard.writeText(updated)
}

export const removeSearchParams = (lnk: string, params: string[]) => {
  if (params.length < 1) return lnk
  const url = new URL(lnk)
  params.forEach((param) => {
    url.searchParams.delete(param)
  })
  return url.toString()
}

export const checkIfEmbededInNativeApp = () => {
  if (/WKWebView/.test(navigator.userAgent)) {
    // Running in a WKWebView iOS
    return true
  } else if (/WebView/.test(navigator.userAgent)) {
    // Running in a UIWebView on iOS (older versions) or Android
    return true
  } else if (checkIfEmbededInPLLApp()) {
    // Running in PLL App
    return true
  } else if (window && window?.top !== window) {
    // Last check, if window.top is not equal to window, then we are in an iframe
    return true
  } else {
    // Not in an iframe
    return false
  }
}

/**
 * Use user agent to check if running in PLL App
 */
export const checkIfEmbededInPLLApp = () => {
  if (/pllmobile/.test(navigator.userAgent)) {
    // Running in PLL App
    return true
  }
  if (/pllmobile_android/.test(navigator.userAgent)) {
    // Running in PLL App
    return true
  }
  if (/pllmobile_iOS/.test(navigator.userAgent)) {
    // Running in PLL App
    return true
  }
  return false
}

export const checkStorageOptions = (): {
  indexedDB: boolean
  local: boolean
  session: boolean
  cookies: boolean
} => {
  const indexedDBEnabled = isIndexedDBAvailable()
  const localStorageEnabled = isLocalStorageEnabled()
  const sessionStorageEnabled = isSessionStorageEnabled()
  const cookiesEnabled = isCookiesEnabled()
  return {
    indexedDB:indexedDBEnabled,
    local: localStorageEnabled,
    session: sessionStorageEnabled,
    cookies: cookiesEnabled,
  }
}

export const isIndexedDBAvailable = (): boolean => {
  return typeof window !== 'undefined' && 'indexedDB' in window
}

export const isLocalStorageEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    // Environment does not have window
    return false
  }
  try {
    const testKey = 'test'
    localStorage.setItem(testKey, testKey)
    localStorage.removeItem(testKey)
    return true
  } catch (e) {
    return false
  }
}

export const isSessionStorageEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    // Environment does not have window
    return false
  }
  try {
    const testKey = 'test'
    sessionStorage.setItem(testKey, testKey)
    sessionStorage.removeItem(testKey)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Checks if cookies are enabled in the user's browser.
 * @returns {boolean} True if cookies are enabled, false otherwise.
 */
export const isCookiesEnabled = (): boolean => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false
  }

  try {
    const testCookie = 'pll_test_cookie'
    document.cookie = `${testCookie}=1; SameSite=Strict;`
    const cookies = document.cookie.split(';').map((cookie) => cookie.trim())
    const hasTestCookie = cookies.some((cookie) => cookie.startsWith(`${testCookie}=`))

    if (hasTestCookie) {
      // Delete test cookie, set to past
      document.cookie = `${testCookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      return true
    } else {
      return false
    }
  } catch (e) {
    return false
  }
}

/**
 * Normalize page name to clean pathname
 */
export const normalizePageName = (value?: string) => {
  if (!value) return undefined
  const v = value.trim()
  if (!v) return undefined
  const parts = v.split('/').filter(Boolean)
  return parts.join('_').toLowerCase()
}
